import React, { useMemo, useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { Package, Calendar, MapPin, Map as MapIcon, Users, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { findStadium } from '../../data/stadiums';
import { getMatchTimeFR } from '../../data/wc2026Calendar';
import StadiumPlanModal from '../tickets/StadiumPlanModal';
import DeltaDetailView from './DeltaDetailView';

// Format date to DD/MM/YYYY consistently
function formatDateFR(dateStr) {
  if (!dateStr) return '';
  // Already in DD/MM/YYYY format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  // ISO format YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const [y, m, d] = dateStr.substring(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  return dateStr;
}

const StockValue = ({ value, onClick, clickable }) => {
  const num = parseInt(value) || 0;
  return (
    <span 
      onClick={onClick}
      className={cn(
        'font-mono font-bold text-lg',
        num > 0 && 'text-emerald-600 dark:text-emerald-400',
        num < 0 && 'text-red-600 dark:text-red-400',
        num === 0 && 'text-zinc-500',
        clickable && num !== 0 && 'cursor-pointer hover:scale-110 transition-transform'
      )}
    >
      {num > 0 ? `+${num}` : num}
    </span>
  );
};

// Extract match number from event name (M2, M48, M99, etc.)
const extractMatchNumber = (eventName) => {
  if (!eventName) return 9999;
  const match = eventName.match(/\b[M]?0*(\d{1,3})\b/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  return 9999;
};

// Extract phase name from event (Quarterfinal #4, Men's Final, etc.) - FOR ROLAND GARROS
const extractPhaseName = (eventName) => {
  if (!eventName) return null;
  
  // Common Roland Garros phases - ORDER MATTERS! Check more specific patterns first
  const phasePatterns = [
    // Semifinals with number - most specific
    /Men's Semifinal #\d/i,
    /Women's Semifinal #?\d?/i,
    /Semifinal\s*#\d/i,
    // Quarterfinals
    /Quarterfinal\s*#?\d/i,
    /Quarterfinals/i,
    /Quart\s*de\s*finale/i,
    // Semifinals generic
    /Semifinals/i,
    /Demi-finale/i,
    // Finals - Women's BEFORE Men's to avoid "WOMAN" matching "MAN"
    /Women's Final/i,
    /Men's Final/i,
    /Finale/i,
    // Rounds
    /1st Round/i,
    /2nd Round/i,
    /3rd Round/i,
    /4th Round/i,
    /Round\s*\d/i,
    /1er Tour/i,
    /2e Tour/i,
    /3e Tour/i,
    /4e Tour/i,
  ];
  
  for (const pattern of phasePatterns) {
    const match = eventName.match(pattern);
    if (match) {
      return match[0];
    }
  }
  
  // Try to extract "Day X - [Phase]" pattern
  const dayPhaseMatch = eventName.match(/Day\s*\d+\s*[-–—]\s*([^-–—]+)/i);
  if (dayPhaseMatch && dayPhaseMatch[1]) {
    const phase = dayPhaseMatch[1].trim();
    // Only return if it looks like a phase name
    if (phase.length > 3 && !phase.toLowerCase().includes('session') && !phase.toLowerCase().includes('chatrier') && !phase.toLowerCase().includes('lenglen')) {
      return phase;
    }
  }
  
  return null;
};

// Format event name - extract just the team names and phase
// "M2 KOREA VS TBD - FIFA WORLD CUP 2026 - GROUP A" -> "KOREA VS TBD - GROUP A"
import { resolveEventNameGlobal, resolveEventByApi } from '../../utils/resolveEventName';

// Nettoyage commun (retrait préfixes M{N}, "FIFA WORLD CUP", tirets multiples…)
const _cleanupEventText = (text) => {
  let cleaned = (text || '').replace(/\s+/g, ' ').trim();
  cleaned = cleaned.replace(/^M\d+\s*[-–—]?\s*/i, '');
  cleaned = cleaned.replace(/\bFIFA\s*WORLD\s*CUP\s*2026?\b/gi, '').trim();
  cleaned = cleaned.replace(/\bWORLD\s*CUP\s*2026?\b/gi, '').trim();
  cleaned = cleaned.replace(/\bWC\s*2026?\b/gi, '').trim();
  cleaned = cleaned.replace(/\s*[-–—]+\s*/g, ' - ').trim();
  cleaned = cleaned.replace(/^\s*[-–—]+\s*/, '').trim();
  cleaned = cleaned.replace(/\s*[-–—]+\s*$/, '').trim();
  cleaned = cleaned.replace(/\s+/g, ' ');
  return cleaned;
};

// Rugby : "M4 -( POOL E ) - France vs USA" → { stage: "POOL E", teams: "France vs USA" }
const parseRugbyName = (evenement) => {
  const m = (evenement || '').match(/^M\d+\s*-\(\s*([^)]*)\)\s*-\s*(.+)$/i);
  if (m) return { stage: m[1].trim(), teams: m[2].trim() };
  return { stage: null, teams: _cleanupEventText(evenement || '') };
};

// Version texte simple (sans détection de doute) — pour compat avec les anciens call sites
const formatEventNameForDisplay = (eventName, matchNum) => {
  if (!eventName) return '';
  return _cleanupEventText(resolveEventNameGlobal(eventName));
};

// 🆕 Version avancée : croise avec api-football si date_evenement fourni.
// Retourne { text, doute, reason } — la vue peut afficher un badge ⚠️ DOUTE.
const formatEventNameWithDoute = (eventName, matchNum, dateEvenement, extras) => {
  if (!eventName) return { text: '', doute: false };
  // 🎯 Injecte le timestamp UTC du coup d'envoi depuis le calendrier hardcodé WC2026.
  // Sans ça, le helper se base sur l'heure de `date_evenement` qui peut être l'heure de
  // listing/vente Viagogo (et non l'heure réelle du match) — cause des DOUTE en prod.
  const tFR = (typeof matchNum === 'number' && matchNum > 0) ? getMatchTimeFR(matchNum) : null;
  const enriched = { ...(extras || {}), matchStartUTC: tFR?.startUTC || null };
  const r = resolveEventByApi(eventName, dateEvenement, enriched);
  return { text: _cleanupEventText(r.text), doute: r.doute, reason: r.reason, source: r.source };
};

export const DeltaView = ({ data = [], onCategoryClick, project = '', viewMode: viewModeProp = 'cards', onViewModeChange, allTickets = [] }) => {
  const isWC = project && project.toUpperCase().startsWith('WORLD CUP');
  const isRugbyProject = (project || '').toUpperCase().includes('RUGBY');
  
  // Persistence key (per project)
  const persistKey = `deltaView_state_${project}`;
  const loadPersisted = () => {
    try {
      const raw = sessionStorage.getItem(persistKey);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  };
  const persisted = loadPersisted();
  
  // Stadium plan modal state
  const [stadiumPlanOpen, setStadiumPlanOpen] = useState(false);
  const [selectedStadium, setSelectedStadium] = useState(null);
  const [selectedMatchInfo, setSelectedMatchInfo] = useState(null);
  
  // Filter state (restored from sessionStorage if available)
  const [filterCourt, setFilterCourt] = useState(persisted.filterCourt ?? 'all');
  const [filterSession, setFilterSession] = useState(persisted.filterSession ?? 'all');
  // Dates explicitement masquées (RG : set de chaînes date_evenement)
  const [hiddenDates, setHiddenDates] = useState(() => new Set(Array.isArray(persisted.hiddenDates) ? persisted.hiddenDates : []));
  // Matchs explicitement masqués (WC : set de numéros de match)
  const [hiddenMatches, setHiddenMatches] = useState(() => new Set(Array.isArray(persisted.hiddenMatches) ? persisted.hiddenMatches : []));
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const dateFilterRef = useRef(null);
  // WC: multi-select venues + match range
  const [selectedVenues, setSelectedVenues] = useState(new Set(persisted.selectedVenues || []));
  const [matchRange, setMatchRange] = useState(persisted.matchRange ?? null); // null = all, 10 = M10-M19, etc.
  const [matchNumSearch, setMatchNumSearch] = useState(persisted.matchNumSearch ?? ''); // exact match number search (WC)
  const [venueDropdownOpen, setVenueDropdownOpen] = useState(false);
  const venueDropdownRef = useRef(null);

  // 🕐 Auto-masquage 4h après coup d'envoi (WC uniquement) — toggle pour voir les matchs passés
  const [showPastMatches, setShowPastMatches] = useState(persisted.showPastMatches ?? false);
  // Re-render toutes les 60s pour que le "past" se réactualise en live
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // Persist filters whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(persistKey, JSON.stringify({
        filterCourt,
        filterSession,
        selectedVenues: Array.from(selectedVenues),
        matchRange,
        matchNumSearch,
        hiddenDates: Array.from(hiddenDates),
        hiddenMatches: Array.from(hiddenMatches),
        showPastMatches,
        scrollY: window.scrollY,
      }));
    } catch {}
  }, [filterCourt, filterSession, selectedVenues, matchRange, matchNumSearch, hiddenDates, hiddenMatches, showPastMatches, persistKey]);

  // Restore scroll position on mount (with small delay to let content render)
  useEffect(() => {
    const y = persisted.scrollY;
    if (typeof y === 'number' && y > 0) {
      const t1 = setTimeout(() => window.scrollTo(0, y), 50);
      const t2 = setTimeout(() => window.scrollTo(0, y), 250);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save scroll position before leaving (on click into a category)
  const handleSaveScroll = () => {
    try {
      const existing = loadPersisted();
      sessionStorage.setItem(persistKey, JSON.stringify({
        ...existing,
        filterCourt,
        filterSession,
        selectedVenues: Array.from(selectedVenues),
        matchRange,
        scrollY: window.scrollY,
      }));
    } catch {}
  };
  
  // View mode: 'cards' or 'table' (VUE DRIVE) - controlled by parent
  const viewMode = viewModeProp;
  const setViewMode = onViewModeChange || (() => {});
  const [tableFilter, setTableFilter] = useState('all'); // 'all', 'need', 'stock'
  const [tableDetailed, setTableDetailed] = useState(false); // si true, affiche "3+3+6+8" au lieu de "-20" (Drive uniquement)
  
  // Detail expansion state (for old inline detail)
  const [expandedCell, setExpandedCell] = useState(null);
  const [expandedTickets, setExpandedTickets] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Detail view (imported at top level)
  // No lazy loading to avoid re-render loops
  
  // Crosshair hover state for VUE DRIVE
  const [hoveredCell, setHoveredCell] = useState(null); // { row, col, matchLabel, catLabel }
  
  // Close venue dropdown when clicking outside
  useEffect(() => {
    if (!venueDropdownOpen) return;
    const handler = (e) => {
      if (venueDropdownRef.current && !venueDropdownRef.current.contains(e.target)) {
        setVenueDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [venueDropdownOpen]);
  
  // Extract unique courts/venues and sessions from data
  const { courts, sessions, venues, availableDates, availableMatches, filteredData } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return { courts: [], sessions: [], venues: [], availableDates: [], availableMatches: [], filteredData: [] };
    
    const courtSet = new Set();
    const sessionSet = new Set();
    const venueSet = new Set();
    
    data.forEach(event => {
      const lieu = (event.lieu || '').trim();
      if (isWC) {
        // For WC: collect all unique venue names
        if (lieu) venueSet.add(lieu);
      } else {
        // For RG: group by known courts
        const lieuLower = lieu.toLowerCase();
        if (lieuLower.includes('chatrier')) courtSet.add('Chatrier');
        else if (lieuLower.includes('lenglen')) courtSet.add('Lenglen');
        else if (lieuLower.includes('outside') || lieuLower.includes('annexe')) courtSet.add('Outside');
        else if (lieu) courtSet.add(lieu);
      }
      
      const sess = event.session || '';
      if (sess && sess !== '-') {
        if (sess.toLowerCase().includes('day') || sess.toLowerCase().includes('jour')) sessionSet.add('Day');
        else if (sess.toLowerCase().includes('evening') || sess.toLowerCase().includes('night')) sessionSet.add('Evening');
      }
    });
    
    // Apply filters
    let filtered = data;
    
    if (isWC) {
      // WC: multi-select venue filter
      if (selectedVenues.size > 0) {
        filtered = filtered.filter(event => {
          const lieu = (event.lieu || '').trim();
          return selectedVenues.has(lieu);
        });
      }
      // WC: match range filter
      if (matchRange !== null) {
        filtered = filtered.filter(event => {
          const mn = event.match_num || extractMatchNumber(event.evenement);
          return mn >= matchRange && mn < matchRange + 10;
        });
      }
      // WC: search by exact match number (e.g. "45" → only M45)
      const searchTrim = String(matchNumSearch || '').trim();
      if (searchTrim) {
        const searchNum = parseInt(searchTrim.replace(/^M/i, ''), 10);
        if (!isNaN(searchNum)) {
          filtered = filtered.filter(event => {
            const mn = event.match_num || extractMatchNumber(event.evenement);
            return mn === searchNum;
          });
        }
      }
    } else {
      // RG: single court filter
      if (filterCourt !== 'all') {
        filtered = filtered.filter(event => {
          const lieu = (event.lieu || '').toLowerCase();
          if (filterCourt === 'Chatrier') return lieu.includes('chatrier');
          if (filterCourt === 'Lenglen') return lieu.includes('lenglen');
          if (filterCourt === 'Outside') return lieu.includes('outside') || lieu.includes('annexe');
          return lieu.includes(filterCourt.toLowerCase());
        });
      }
      if (filterSession !== 'all') {
        filtered = filtered.filter(event => {
          const sess = (event.session || '').toLowerCase();
          if (filterSession === 'Day') return sess.includes('day') || sess.includes('jour');
          if (filterSession === 'Evening') return sess.includes('evening') || sess.includes('night');
          return true;
        });
      }
    }

    // Filtres "Cacher" :
    // - RG : par date_evenement
    // - WC : par numéro de match
    if (!isWC && hiddenDates && hiddenDates.size > 0) {
      filtered = filtered.filter(event => !hiddenDates.has(event.date_evenement || ''));
    }
    if (isWC && hiddenMatches && hiddenMatches.size > 0) {
      filtered = filtered.filter(event => {
        const mn = event.match_num || extractMatchNumber(event.evenement);
        return !hiddenMatches.has(mn);
      });
    }

    // 🕐 Auto-masquage WC : retirer les matchs dont le coup d'envoi remonte à plus de 4h
    // (sauf si l'utilisateur a explicitement activé "Afficher matchs passés")
    if (isWC && !showPastMatches) {
      const nowMs = Date.now();
      const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
      filtered = filtered.filter(event => {
        const mn = event.match_num || extractMatchNumber(event.evenement);
        if (!mn || mn === 9999) return true; // pas de numéro de match → on garde
        const t = getMatchTimeFR(mn);
        if (!t || !t.startUTC) return true; // info manquante → on garde par sécurité
        return nowMs <= t.startUTC + FOUR_HOURS_MS;
      });
    }
    
    // Collecte des dates uniques (pour le picker RG)
    const dateSet = new Set();
    data.forEach(event => {
      if (event.date_evenement) dateSet.add(event.date_evenement);
    });

    // Collecte des matchs uniques (pour le picker WC) : numero + label
    const matchMap = new Map();
    data.forEach(event => {
      const mn = event.match_num || extractMatchNumber(event.evenement);
      if (mn === undefined || mn === null || mn === 9999) return;
      if (!matchMap.has(mn)) {
        matchMap.set(mn, {
          num: mn,
          evenement: event.evenement || `Match ${mn}`,
          date: event.date_evenement || '',
          lieu: event.lieu || '',
        });
      }
    });
    const availableMatches = Array.from(matchMap.values()).sort((a, b) => a.num - b.num);

    return {
      courts: Array.from(courtSet).sort(),
      sessions: Array.from(sessionSet).sort(),
      venues: Array.from(venueSet).sort(),
      availableDates: Array.from(dateSet).sort((a, b) => {
        const parse = (d) => {
          if (!d) return 0;
          if (/^\d{2}\/\d{2}\/\d{4}/.test(d)) {
            const [dd, mm, yy] = d.substring(0,10).split('/');
            return new Date(`${yy}-${mm}-${dd}`).getTime();
          }
          return new Date(d).getTime();
        };
        return parse(a) - parse(b);
      }),
      availableMatches,
      filteredData: filtered
    };
  }, [data, filterCourt, filterSession, selectedVenues, matchRange, matchNumSearch, isWC, hiddenDates, hiddenMatches, showPastMatches]);
  
  const sortedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    return filteredData;
  }, [filteredData]);

  // 🚨 Détection de doublons de titre (Fév 2026) : si 2+ cartes affichent
  // EXACTEMENT le même libellé final (après résolution), on forcera la
  // différenciation par stade + on affichera un badge ⚠ TITRE DUPLIQUÉ.
  // Cas typique : tickets dont l'`evenement` brut = "Pays-Bas vs Maroc"
  // existe pour 3 match_num différents (M77/M78/M79) → l'utilisateur ne
  // sait plus distinguer. On force l'affichage de stade + brut.
  const duplicateEventNames = useMemo(() => {
    const counts = {};
    for (const ev of sortedData) {
      if (!ev || !ev.evenement) continue;
      const finalText = formatEventNameWithDoute(ev.evenement, ev.match_num, ev.date_evenement, { lieu: ev.lieu, ville: ev.ville }).text;
      const key = (finalText || '').toLowerCase().trim();
      if (!key) continue;
      counts[key] = (counts[key] || 0) + 1;
    }
    return new Set(Object.entries(counts).filter(([, n]) => n > 1).map(([k]) => k));
  }, [sortedData]);

  // Export dropdown state
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Export Delta as CSV table (Match | Achat | Vente | Cat1 | Cat2 | ...)
  // mode: 'all' = everything, 'need' = only negatives (red), 'stock' = only positives (green)
  const handleExportDelta = (mode = 'all') => {
    if (!sortedData || sortedData.length === 0) return;
    setShowExportMenu(false);

    // Helper: format a value with breakdown if detailed mode is ON
    // Affiche les quantités en valeur absolue séparées par "+" (ex: 3+3+6+8)
    const formatVal = (val, mn, cat, eventCard) => {
      if (val === 0) return '';
      if (!tableDetailed) return val;
      const bd = getBreakdown(mn, cat, eventCard);
      const arr = val < 0 ? (bd?.ventes || []) : (bd?.achats || []);
      if (!arr || arr.length <= 1) return val;
      return arr.map(q => Math.abs(q)).join('+');
    };
    
    // 1. Collect all unique categories across all events
    const allCategories = new Set();
    sortedData.forEach(event => {
      if (event.stocks) {
        Object.keys(event.stocks).forEach(cat => allCategories.add(cat));
      }
    });
    
    // Sort categories: GOLD first, then CAT1, CAT2, CAT3, CAT4, then alphabetical
    const catOrder = (cat) => {
      const c = cat.toUpperCase().replace(/[ÉE]/g, 'E').replace(/CATEGORIE\s*/g, 'CAT').trim();
      if (/^CAT\s*1$/i.test(c)) return 1;
      if (/^CAT\s*2$/i.test(c)) return 2;
      if (/^CAT\s*3$/i.test(c)) return 3;
      if (/^CAT\s*4$/i.test(c)) return 4;
      if (c === 'GOLD') return 0;
      return 100;
    };
    const sortedCategories = Array.from(allCategories).sort((a, b) => {
      const oa = catOrder(a), ob = catOrder(b);
      if (oa !== ob) return oa - ob;
      return a.localeCompare(b);
    });
    
    // 2. Detect if this is Roland Garros (has sessions)
    const isRG = sortedData.some(e => e.session && e.session !== '' && e.session !== '-');
    
    // 3. Build raw rows
    let headers, rawRows;
    
    if (isRG) {
      const sortedEvents = [...sortedData].sort((a, b) => {
        const mnA = a.match_num || 0, mnB = b.match_num || 0;
        if (mnA !== mnB) return mnA - mnB;
        const sessA = (a.session || '').toLowerCase();
        const sessB = (b.session || '').toLowerCase();
        if (sessA.includes('day') && sessB.includes('evening')) return -1;
        if (sessA.includes('evening') && sessB.includes('day')) return 1;
        return 0;
      });
      
      headers = ['Match', 'Nom du Match', 'Session', 'Court', 'Achat', 'Vente', ...sortedCategories];
      const metaCols = 4; // Match, Nom, Session, Court before Achat/Vente
      rawRows = sortedEvents.map(event => {
        const mn = event.match_num || 0;
        const session = (event.session || '').replace(' Session', '');
        const court = event.lieu || '';
        const stocks = event.stocks || {};
        const catValues = sortedCategories.map(cat => stocks[cat] || 0);
        return { meta: [`M${mn}`, event.evenement || '', session, court], catValues, mn, eventCard: event };
      });
    } else {
      const matchMap = {};
      sortedData.forEach(event => {
        const mn = event.match_num || extractMatchNumber(event.evenement);
        if (!matchMap[mn]) {
          matchMap[mn] = { matchNum: mn, name: event.evenement || '', stocks: {}, eventCard: event };
        }
        if (event.stocks) {
          Object.entries(event.stocks).forEach(([cat, stock]) => {
            matchMap[mn].stocks[cat] = (matchMap[mn].stocks[cat] || 0) + stock;
          });
        }
      });
      const sortedMatches = Object.values(matchMap).sort((a, b) => a.matchNum - b.matchNum);
      
      headers = ['Match', 'Nom du Match', 'Achat', 'Vente', ...sortedCategories];
      rawRows = sortedMatches.map(m => {
        const stocks = m.stocks;
        const catValues = sortedCategories.map(cat => stocks[cat] || 0);
        return { meta: [`M${m.matchNum}`, m.name || ''], catValues, mn: m.matchNum, eventCard: m.eventCard };
      });
    }
    
    // 4. Apply mode filter
    const rows = [];
    for (const { meta, catValues, mn, eventCard } of rawRows) {
      let filteredCats;
      if (mode === 'need') {
        // Only negative values (red lines), zero out positives
        filteredCats = catValues.map(v => v < 0 ? v : 0);
      } else if (mode === 'stock') {
        // Only positive values (green lines), zero out negatives
        filteredCats = catValues.map(v => v > 0 ? v : 0);
      } else {
        filteredCats = [...catValues];
      }
      
      // Skip row if all category values are 0 after filtering
      if (mode !== 'all' && filteredCats.every(v => v === 0)) continue;
      
      // Calculate Achat/Vente from filtered values
      let achat = 0, vente = 0;
      filteredCats.forEach(val => {
        if (val < 0) achat += val;
        else vente += val;
      });
      
      rows.push([...meta, achat, vente, ...filteredCats, mn, eventCard]);
    }
    
    // 5. Remove columns where ALL values are 0, and replace 0 with empty
    // Determine which category columns to keep (have at least one non-zero value)
    const catStartIdx = isRG ? 4 : 2; // après meta : Match[+Nom][+Session+Court]
    const achatIdx = catStartIdx;      // Achat column index in row
    const venteIdx = catStartIdx + 1;  // Vente column index in row
    const catDataStart = catStartIdx + 2; // category data starts after Achat+Vente
    // mn et eventCard sont stockés à la fin de chaque row (pour formatVal)
    const mnIdx = catDataStart + sortedCategories.length;
    const ecIdx = mnIdx + 1;
    
    // Find which category columns have at least one non-zero value
    const keepCatCol = sortedCategories.map((_, ci) => {
      return rows.some(r => r[catDataStart + ci] !== 0);
    });
    
    // Build filtered headers
    const metaHeaders = isRG ? ['Match', 'Nom du Match', 'Session', 'Court'] : ['Match', 'Nom du Match'];
    const filteredHeaders = [...metaHeaders];
    // Add Achat only if it has non-zero values (skip for STOCK mode)
    const hasAchat = rows.some(r => r[achatIdx] !== 0);
    const hasVente = rows.some(r => r[venteIdx] !== 0);
    if (hasAchat) filteredHeaders.push('Achat');
    if (hasVente) filteredHeaders.push('Vente');
    // Add only non-empty category columns
    sortedCategories.forEach((cat, ci) => {
      if (keepCatCol[ci]) filteredHeaders.push(cat);
    });
    
    // Build filtered rows with 0 replaced by empty string,
    // et application du formatage détaillé (3+3+6+8) si activé
    const filteredRows = rows.map(r => {
      const rowMn = r[mnIdx];
      const rowEc = r[ecIdx];
      const row = [...r.slice(0, catStartIdx)]; // meta columns
      // Achat / Vente : on garde la somme numérique (pas de détail pertinent)
      if (hasAchat) row.push(r[achatIdx] === 0 ? '' : r[achatIdx]);
      if (hasVente) row.push(r[venteIdx] === 0 ? '' : r[venteIdx]);
      sortedCategories.forEach((cat, ci) => {
        if (keepCatCol[ci]) {
          row.push(formatVal(r[catDataStart + ci], rowMn, cat, rowEc));
        }
      });
      return row;
    });
    
    // 6. Convert to CSV string with proper quoting
    const escapeCSV = (val) => {
      const s = String(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes(';')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const csvContent = [
      filteredHeaders.map(escapeCSV).join(';'),
      ...filteredRows.map(r => r.map(escapeCSV).join(';'))
    ].join('\n');
    
    // 7. Download
    const modeLabel = mode === 'need' ? 'NEED' : mode === 'stock' ? 'STOCK' : 'TOUT';
    const detailSuffix = tableDetailed ? '_DETAILLE' : '';
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `DELTA_${modeLabel}${detailSuffix}_${project || 'EXPORT'}_${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCategoryClick = (event, category, stock, forceViewType = null) => {
    // forceViewType permet d'ouvrir explicitement 'achat' ou 'vente' depuis les boutons du tooltip
    if (stock === 0 && !forceViewType) return;
    if (!onCategoryClick) return;
    
    // Save current scroll + filters so user comes back to same spot
    handleSaveScroll();
    
    // Determine if we should show ACHAT (+) or VENTE (-)
    const viewType = forceViewType || (stock > 0 ? 'achat' : 'vente');
    // Use backend-assigned match_num first (reliable), fallback to regex extraction
    const matchNum = event.match_num || extractMatchNumber(event.evenement);
    
    onCategoryClick({
      viewType,
      eventName: event.evenement,
      session: event.session,
      lieu: event.lieu || '',
      matchNum,
      category,
      stock
    });
  };
  
  const handleShowStadiumPlan = (stadium, event, e) => {
    e.stopPropagation();
    setSelectedStadium(stadium);
    
    // Extract match number from event name
    const matchNum = extractMatchNumber(event.evenement);
    const matchNumStr = matchNum !== 9999 ? `M${matchNum}` : null;
    
    setSelectedMatchInfo({
      matchNum: matchNumStr,
      eventName: event.evenement,
      date: formatDateFR(event.date_evenement),
      lieu: event.lieu,
      ville: event.ville
    });
    setStadiumPlanOpen(true);
  };

  // Toggle venue in multi-select
  const toggleVenue = (venue) => {
    setSelectedVenues(prev => {
      const next = new Set(prev);
      if (next.has(venue)) next.delete(venue);
      else next.add(venue);
      return next;
    });
  };
  
  const selectAllVenues = () => setSelectedVenues(new Set(venues));
  const clearVenues = () => setSelectedVenues(new Set());

  const toggleDate = (date) => {
    setHiddenDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };
  const hideAllDates = () => setHiddenDates(new Set(availableDates));
  const showAllDates = () => setHiddenDates(new Set());

  const toggleMatch = (matchNum) => {
    setHiddenMatches(prev => {
      const next = new Set(prev);
      if (next.has(matchNum)) next.delete(matchNum); else next.add(matchNum);
      return next;
    });
  };
  const hideAllMatches = () => setHiddenMatches(new Set(availableMatches.map(m => m.num)));
  const showAllMatches = () => setHiddenMatches(new Set());

  // Format date pour affichage : DD/MM/YYYY ou ISO → DD/MM
  const formatDateShort = (d) => {
    if (!d) return '';
    if (/^\d{2}\/\d{2}\/\d{4}/.test(d)) return d.substring(0, 10);
    if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
      const [y, m, dd] = d.substring(0,10).split('-');
      return `${dd}/${m}/${y}`;
    }
    // Format court "DD-MM" ou "DD/MM" sans année
    if (/^\d{2}[-/]\d{2}$/.test(d)) {
      const sep = d.includes('-') ? '-' : '/';
      const [dd, mm] = d.split(sep);
      const year = new Date().getFullYear();
      return `${dd}/${mm}/${year}`;
    }
    return d;
  };

  // Click outside du dropdown date pour le fermer
  useEffect(() => {
    if (!dateFilterOpen) return;
    const onClick = (e) => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) {
        setDateFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [dateFilterOpen]);

  // For Roland Garros: normalize court and session like backend does, so the tooltip
  // can match the SAME ticket subset as the displayed card.
  const isRG = (project || '').toUpperCase().includes('ROLAN');

  const normalizeCourt = (lieu, evenement) => {
    const l = (lieu || '').toLowerCase();
    const e = (evenement || '').toLowerCase();
    if (l.includes('chatrier') || e.includes('chatrier')) return 'Chatrier';
    if (l.includes('lenglen') || e.includes('lenglen')) return 'Lenglen';
    if (l.includes('outside') || l.includes('annexe') || l.includes('exterieur') ||
        e.includes('outside') || e.includes('annexe')) return 'Outside';
    return '';
  };
  const normalizeSession = (session, evenement) => {
    const s = (session || '').toLowerCase().trim();
    if (s.includes('day') || s.includes('jour')) return 'Day';
    if (s.includes('evening') || s.includes('night') || s.includes('nuit') || s.includes('soir')) return 'Evening';
    const e = (evenement || '').toLowerCase();
    if (e.includes('day session') || e.includes('session jour')) return 'Day';
    if (e.includes('evening session') || e.includes('night session') || e.includes('session soir')) return 'Evening';
    return '';
  };
  // Same merging as backend stats.py: OUTSIDE → Chatrier Day, LENGLEN+ANNEXE → Chatrier Day
  const normalizeCourtSession = (lieuRaw, sessionRaw, eventNameRaw, catRaw) => {
    let court = normalizeCourt(lieuRaw, eventNameRaw);
    let session = normalizeSession(sessionRaw, eventNameRaw);
    const catUpper = (catRaw || '').toUpperCase();
    const isAnnexe = catUpper.includes('ANNEXE') || catUpper.includes('ANNEX');
    if (court === 'Outside') { court = 'Chatrier'; session = 'Day'; }
    if (court === 'Lenglen' && isAnnexe) { court = 'Chatrier'; session = session || 'Day'; }
    if (court === 'Chatrier' && !session) session = 'Day';
    if (!court) court = 'Chatrier'; // fallback for RG
    return { court, session };
  };
  // Normalize a Delta CARD's lieu/session display values back to (court, session) keys
  const normalizeCardCourtSession = (cardLieu, cardSession) => {
    const l = (cardLieu || '').toLowerCase();
    let court = '';
    if (l.includes('chatrier')) court = 'Chatrier';
    else if (l.includes('lenglen')) court = 'Lenglen';
    else court = 'Chatrier';
    const s = (cardSession || '').toLowerCase();
    let session = '';
    if (s.includes('day')) session = 'Day';
    else if (s.includes('evening') || s.includes('night')) session = 'Evening';
    // Lenglen card has no session
    if (court === 'Lenglen') session = '';
    if (court === 'Chatrier' && !session) session = 'Day';
    return { court, session };
  };

  // Build breakdown index keyed by RG-style composite for Roland Garros,
  // simple match_num for World Cup.
  //
  // ⚠️ Gestion des ATTRIBUTIONS PARTIELLES :
  // - Une vente avec attribution_status='partial' et partial_qty < qty a déjà
  //   partial_qty places attribuées via ACHAT. Il ne reste que qty - partial_qty
  //   à fournir. On stocke alors { qty, partial, remaining }.
  // - Si partial_qty >= qty → vente entièrement attribuée, on l'ignore (skip).
  // - Les ventes "normales" (sans partial) ont partial=0, remaining=qty.
  const breakdownIndex = useMemo(() => {
    const idx = {};
    const normCat = (c) => (c || '').toString().toUpperCase().replace(/\s+/g, ' ').replace(/^CATEGORY\s*/, 'CATEGORIE ').trim();
    const isCancelled = (t) => {
      const s = (t.statut || '').toUpperCase();
      return s === 'ANNULEE' || s === 'ANNULÉ' || s === 'ANNULE';
    };
    for (const t of allTickets) {
      if (isCancelled(t)) continue;
      // Exclure les tickets entièrement attribués...
      //   ⚠️ EXCEPTION : une VENTE avec attribution_status='partial' a attribue=true
      //   (mis par le backend pour comptabilité) MAIS il reste partial_qty < qty à fournir.
      //   On la garde dans le Delta pour afficher le reste à fournir.
      if (t.attribue) {
        const isPartialVenteToShow = t.type === 'VENTE'
          && t.attribution_status === 'partial'
          && (parseInt(t.partial_qty, 10) || 0) < (parseInt(t.qty, 10) || 1);
        if (!isPartialVenteToShow) continue;
      }
      const m = (t.evenement || '').match(/M(\d+)/i);
      if (!m) continue;
      const mn = parseInt(m[1], 10);
      const cat = normCat(t.categorie);
      let groupKey;
      if (isRG) {
        const { court, session } = normalizeCourtSession(t.lieu, t.session, t.evenement, t.categorie);
        groupKey = `${mn}|${court}|${session}`;
      } else {
        groupKey = `${mn}`;
      }
      if (!idx[groupKey]) idx[groupKey] = {};
      if (!idx[groupKey][cat]) idx[groupKey][cat] = { ventes: [], achats: [] };
      const q = parseInt(t.qty, 10) || 1;
      if (t.type === 'VENTE') {
        // Lecture des attributions partielles
        const partial = (t.attribution_status === 'partial')
          ? Math.max(0, Math.min(q, parseInt(t.partial_qty, 10) || 0))
          : 0;
        const remaining = q - partial;
        // Si déjà entièrement servi via partial → ne pas afficher (équivalent full)
        if (remaining <= 0) continue;
        idx[groupKey][cat].ventes.push({ qty: q, partial, remaining });
      } else if (t.type === 'ACHAT') {
        idx[groupKey][cat].achats.push({ qty: q });
      }
    }
    // Tri : ventes par remaining décroissant ; achats par qty décroissant
    for (const gk of Object.keys(idx)) {
      for (const cat of Object.keys(idx[gk])) {
        idx[gk][cat].ventes.sort((a, b) => b.remaining - a.remaining);
        idx[gk][cat].achats.sort((a, b) => b.qty - a.qty);
      }
    }
    return idx;
  }, [allTickets, isRG]);

  const getBreakdown = (matchNum, category, eventCard) => {
    const normCat = (c) => (c || '').toString().toUpperCase().replace(/\s+/g, ' ').replace(/^CATEGORY\s*/, 'CATEGORIE ').trim();
    const cat = normCat(category);
    let groupKey;
    if (isRG && eventCard) {
      const { court, session } = normalizeCardCourtSession(eventCard.lieu, eventCard.session);
      groupKey = `${matchNum}|${court}|${session}`;
    } else {
      groupKey = `${matchNum}`;
    }
    return breakdownIndex[groupKey]?.[cat] || { ventes: [], achats: [] };
  };

  // Render a single Delta card (used by both RG grouped and flat layouts)
  const renderDeltaCard = (event, index) => {
    const matchNum = event.match_num || extractMatchNumber(event.evenement);
    const stadium = findStadium(event.lieu, event.ville, event.evenement, project);
    const phaseName = extractPhaseName(event.evenement);
    const isRolandGarros = (event.session && event.session !== '-') || 
      (event.lieu && (event.lieu.includes('Chatrier') || event.lieu.includes('Lenglen') || event.lieu.includes('Outside')));
    const isEarlyRound = phaseName && /^(1st|2nd|3rd|4th)\s*Round$/i.test(phaseName);

    return (
      <div key={`delta-${index}`} data-testid={`delta-card-${index}`} className="delta-card">
        <div className="pb-2 pt-3 px-3">
          {isRugbyProject && (() => {
            const { stage, teams } = parseRugbyName(event.evenement);
            return (
              <div className="mb-1">
                <p className="text-base font-extrabold text-white leading-tight" data-testid={`event-name-${matchNum}`}>
                  {teams || event.evenement}
                </p>
                {stage && (
                  <span className="text-xs text-[#C4A052] font-semibold tracking-wide mt-0.5 block">( {stage} )</span>
                )}
              </div>
            );
          })()}
          {!isRugbyProject && matchNum < 9999 && (
            <div className="mb-1">
              {isRolandGarros && isEarlyRound ? (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bg-[#C4A052] text-black text-sm font-bold px-2 py-1 rounded inline-block w-fit">M{matchNum}</span>
                    {event.date_evenement && (
                      <span className="text-[#C4A052] text-xl font-extrabold tracking-wide">{formatDateFR(event.date_evenement)}</span>
                    )}
                  </div>
                  <span className="text-[10px] text-zinc-500 mt-0.5 block uppercase tracking-wider">{phaseName}</span>
                </>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="bg-[#C4A052] text-black text-sm font-bold px-2 py-1 rounded inline-block w-fit">M{matchNum}</span>
                  {isRolandGarros && phaseName && (
                    <span className="text-[#C4A052] text-sm font-bold">{phaseName}</span>
                  )}
                </div>
              )}
              {isRolandGarros && (
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {event.session && event.session !== '-' && (
                    <span className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded",
                      event.session.toLowerCase().includes('evening') || event.session.toLowerCase().includes('night')
                        ? "bg-purple-600/30 text-purple-300" : "bg-yellow-600/30 text-yellow-300"
                    )}>
                      {event.session.toLowerCase().includes('evening') || event.session.toLowerCase().includes('night') ? '🌙 Evening' : '☀️ Day'}
                    </span>
                  )}
                  {event.lieu && (
                    <span className="text-xs text-zinc-400">
                      {event.lieu.includes('Chatrier') ? '🏟️ Chatrier' : event.lieu.includes('Lenglen') ? '🏟️ Lenglen' : `🏟️ ${event.lieu}`}
                    </span>
                  )}
                </div>
              )}
              {!isRolandGarros && event.evenement && (() => {
                const ev = formatEventNameWithDoute(event.evenement, matchNum, event.date_evenement, { lieu: event.lieu, ville: event.ville });
                const finalKey = (ev.text || '').toLowerCase().trim();
                const isDuplicate = duplicateEventNames.has(finalKey);
                // Différenciateur visuel : stade (utile car ≠ par match) + libellé brut tronqué.
                // findStadium() retourne un OBJET — on extrait .name pour le rendu React.
                const venue = (typeof stadium === 'object' && stadium !== null ? stadium.name : stadium) || event.lieu || '';
                const rawSnippet = (event.evenement || '').replace(/^\s*M\d+\s*[-–—:]?\s*/i, '').replace(/\s+/g, ' ').trim();
                return (
                  <p className="text-xs font-medium text-white mt-1.5 leading-tight" data-testid={`event-name-${matchNum}`}>
                    {ev.text}
                    {ev.doute && (
                      <span
                        className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 align-middle"
                        title={ev.reason || 'Plusieurs matchs possibles pour cette date — vérification manuelle requise'}
                        data-testid={`event-doute-${matchNum}`}
                      >
                        ⚠ DOUTE
                      </span>
                    )}
                    {isDuplicate && (
                      <>
                        <span
                          className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-300 border border-red-500/40 align-middle"
                          title={`Plusieurs matchs partagent ce libellé — différencié par stade : ${venue}`}
                          data-testid={`event-dup-${matchNum}`}
                        >
                          ⚠ TITRE DUPLIQUÉ
                        </span>
                        {venue && (
                          <span className="block text-[11px] text-amber-300 mt-0.5" data-testid={`event-venue-suffix-${matchNum}`}>
                            🏟 M{matchNum} · {venue}
                          </span>
                        )}
                        {rawSnippet && rawSnippet.toLowerCase() !== (ev.text || '').toLowerCase() && (
                          <span className="block text-[10px] text-zinc-500 mt-0.5 italic" data-testid={`event-raw-${matchNum}`}>
                            brut : {rawSnippet}
                          </span>
                        )}
                      </>
                    )}
                  </p>
                );
              })()}
            </div>
          )}
          {!isRugbyProject && matchNum >= 9999 && event.evenement && (
            <p className="text-xs font-medium text-white mb-1">{event.evenement}</p>
          )}
          <div className="flex flex-wrap gap-2 text-xs text-zinc-400 mt-1">
            {event.date_evenement && !(isRolandGarros && isEarlyRound) && (
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDateFR(event.date_evenement)}</span>
            )}
            {/* Heure française (WC uniquement) — calculée depuis l'heure locale + fuseau IANA.
                Si tu veux retirer cette feature, supprime juste ce bloc. */}
            {isWC && matchNum < 9999 && (() => {
              const tFR = getMatchTimeFR(matchNum);
              if (!tFR) return null;
              // 📋 Listing Viagogo : ouvre jusqu'à 4h avant le coup d'envoi
              const nowMs = Date.now();
              const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
              const isListingClosed = tFR.startUTC && nowMs >= tFR.startUTC - FOUR_HOURS_MS;
              return (
                <>
                  <span
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30 font-semibold"
                    title="Heure de Paris (Europe/Paris)"
                    data-testid={`match-time-fr-${matchNum}`}
                  >
                    🇫🇷 {tFR.label}
                  </span>
                  {tFR.listingClosesLabel && (
                    <span
                      className={cn(
                        'flex items-center gap-1 px-1.5 py-0.5 rounded border font-semibold',
                        isListingClosed
                          ? 'bg-red-500/15 text-red-300 border-red-500/40'
                          : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                      )}
                      title={isListingClosed
                        ? 'Listing Viagogo FERMÉ (≤ 4 h avant coup d\'envoi)'
                        : `Listing Viagogo ouvert jusqu'à ${tFR.listingClosesLabel} (heure de Paris)`}
                      data-testid={`listing-closes-${matchNum}`}
                    >
                      📋 {isListingClosed ? 'Listing fermé' : `Listing jusqu'à ${tFR.listingClosesLabel}`}
                    </span>
                  )}
                </>
              );
            })()}
            {event.ville && (
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{event.ville}</span>
            )}
            {stadium && (
              <Button size="sm" variant="outline" onClick={(e) => handleShowStadiumPlan(stadium, event, e)}
                className="h-6 px-2 text-xs border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10 ml-2 shrink-0">
                <MapIcon className="h-3 w-3 mr-1" />Plan
              </Button>
            )}
          </div>
          {!isRolandGarros && (event.lieu || event.session) && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {event.lieu && <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">{event.lieu}</span>}
              {event.session && event.session !== '-' && <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">{event.session}</span>}
            </div>
          )}
        </div>
        <div className="pt-1 px-3 pb-3">
          <div className="space-y-1">
            {event.stocks && Object.keys(event.stocks).length > 0 ? (
              Object.entries(event.stocks)
                .sort((a, b) => {
                  const order = (cat) => {
                    const c = cat.toUpperCase().replace(/[ÉE]/g, 'E').replace(/CATEGORIE/g, 'CAT').trim();
                    if (c === 'GOLD') return 0;
                    if (/^CAT\s*1$/.test(c) || c === 'CATEGORIE 1') return 10;
                    if (/^CAT\s*1\s*VR$/.test(c)) return 11;
                    if (/^CAT\s*2$/.test(c) || c === 'CATEGORIE 2') return 20;
                    if (/^CAT\s*2\s*VR$/.test(c)) return 21;
                    if (/^CAT\s*3$/.test(c) || c === 'CATEGORIE 3') return 30;
                    if (/^CAT\s*3\s*VR$/.test(c)) return 31;
                    if (/^CAT\s*4$/.test(c) || c === 'CATEGORIE 4') return 40;
                    if (/^CAT\s*RV$/.test(c) || c.includes('RV')) return 45;
                    if (c === 'ANNEXE' || c.includes('ANNEX')) return 50;
                    return 100;
                  };
                  return order(a[0]) - order(b[0]) || a[0].localeCompare(b[0]);
                })
                .map(([category, stock]) => {
                  const bd = getBreakdown(matchNum, category, event);
                  // Totaux : venteRemaining = ce qu'il reste à fournir (hors partial déjà servi)
                  //          venteOrdered    = volume total commandé (info)
                  //          ventePartial    = volume déjà servi via partial attribution
                  const venteRemaining = bd.ventes.reduce((s, v) => s + (v.remaining || 0), 0);
                  const venteOrdered = bd.ventes.reduce((s, v) => s + (v.qty || 0), 0);
                  const ventePartial = bd.ventes.reduce((s, v) => s + (v.partial || 0), 0);
                  const achatTotal = bd.achats.reduce((s, a) => s + (a.qty || 0), 0);
                  // ANNEXE and Lenglen cards have special backend aggregation rules
                  // (OUTSIDE→Chatrier Day, LENGLEN+ANNEXE→Chatrier Day) that the frontend
                  // breakdown cannot replicate accurately. Hide the tooltip for these.
                  const catUpper = String(category || '').toUpperCase();
                  const lieuUpper = String(event?.lieu || '').toUpperCase();
                  const isAnnexeOrLenglen = catUpper.includes('ANNEXE') || catUpper.includes('ANNEX') || lieuUpper.includes('LENGLEN');
                  const hasBreakdown = (bd.ventes.length > 0 || bd.achats.length > 0) && !isAnnexeOrLenglen;
                  const row = (
                    <div key={category} data-testid={`delta-category-${matchNum}-${category}`}
                      onClick={() => handleCategoryClick(event, category, stock)}
                      className={cn("flex items-center justify-between py-1 px-2 rounded bg-zinc-800",
                        stock !== 0 && onCategoryClick && "cursor-pointer hover:bg-zinc-700 transition-colors")}>
                      <span className="text-xs text-zinc-300">{category}</span>
                      <StockValue value={stock} clickable={!!onCategoryClick && stock !== 0} />
                    </div>
                  );
                  if (!hasBreakdown) return row;
                  return (
                    <TooltipProvider key={category} delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>{row}</TooltipTrigger>
                        <TooltipContent side="top" align="end" sideOffset={12} avoidCollisions={false} className="bg-zinc-900 border-2 border-[#C4A052]/40 text-white px-4 py-3 max-w-md shadow-2xl">
                          <div className="space-y-2 text-sm">
                            <div className="border-b border-zinc-700 pb-1.5 mb-1">
                              <div className="font-bold text-[#C4A052] text-base">M{matchNum} · {category}</div>
                              <div className="text-xs text-zinc-400 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                                {event?.date_evenement && (
                                  <span>📅 {(() => {
                                    const d = event.date_evenement;
                                    if (/^\d{2}\/\d{2}\/\d{4}/.test(d)) return d.substring(0, 10);
                                    if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
                                      const [y, m, dd] = d.substring(0,10).split('-');
                                      return `${dd}/${m}/${y}`;
                                    }
                                    return d;
                                  })()}</span>
                                )}
                                {event?.session && <span>🕐 {event.session}</span>}
                                {event?.lieu && <span>🎾 {event.lieu.replace(/Court Philippe /i, '').replace(/Court Suzanne /i, '')}</span>}
                              </div>
                            </div>
                            {bd.ventes.length > 0 && (
                              <div className="leading-relaxed">
                                <div className="text-red-400 font-bold uppercase text-xs tracking-wider mb-1">VENTE à fournir</div>
                                <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                                  {bd.ventes.map((v, i) => (
                                    <span key={i} className="inline-flex items-baseline">
                                      {i > 0 && <span className="text-zinc-500 mr-1.5">+</span>}
                                      {v.partial > 0 ? (
                                        // Ligne avec attribution partielle : [partial bleu]+[restant rouge]
                                        <span className="inline-flex items-baseline rounded px-1.5 py-0.5 bg-sky-500/10 border border-sky-400/30" title={`${v.partial} déjà attribué(s) en partiel · reste ${v.remaining} à fournir`}>
                                          <span className="text-sky-300 font-bold text-base">{v.partial}</span>
                                          <span className="text-zinc-500 mx-0.5">+</span>
                                          <span className="text-red-300 font-bold text-base">{v.remaining}</span>
                                        </span>
                                      ) : (
                                        <span className="text-red-300 font-bold text-lg">{v.remaining}</span>
                                      )}
                                    </span>
                                  ))}
                                  <span className="text-zinc-400 mx-1">=</span>
                                  <span className="text-red-300 font-extrabold text-xl">{venteRemaining}</span>
                                  {ventePartial > 0 && (
                                    <span className="text-zinc-500 text-xs ml-1">à fournir</span>
                                  )}
                                  <span className="text-zinc-500 text-xs ml-1">({bd.ventes.length} ligne{bd.ventes.length > 1 ? 's' : ''})</span>
                                </div>
                                {ventePartial > 0 && (
                                  <div className="text-[11px] text-sky-300/80 mt-1 flex items-center gap-1.5">
                                    <span className="inline-block w-2 h-2 rounded-full bg-sky-400" />
                                    {ventePartial} déjà attribué{ventePartial > 1 ? 's' : ''} en partiel · {venteOrdered} commandé{venteOrdered > 1 ? 's' : ''} au total
                                  </div>
                                )}
                              </div>
                            )}
                            {bd.achats.length > 0 && (
                              <div className="leading-relaxed">
                                <div className="text-emerald-400 font-bold uppercase text-xs tracking-wider mb-1">STOCK ACHAT</div>
                                <div>
                                  <span className="text-emerald-300 font-extrabold text-xl">{achatTotal}</span>
                                  <span className="text-zinc-500 text-xs ml-2">billet{achatTotal > 1 ? 's' : ''} · {bd.achats.length} ligne{bd.achats.length > 1 ? 's' : ''}</span>
                                </div>
                              </div>
                            )}
                            {bd.ventes.length === 0 && bd.achats.length > 0 && (
                              <div className="text-zinc-500 italic text-xs">Aucune vente en attente</div>
                            )}
                            {/* Boutons rapides : voir les VENTE / voir le STOCK */}
                            <div className="flex items-center gap-1.5 pt-2 mt-1 border-t border-zinc-700">
                              {bd.ventes.length > 0 && (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleCategoryClick(event, category, stock, 'vente'); }}
                                  className="h-6 px-2 text-[10px] font-bold bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40"
                                  data-testid={`tooltip-view-ventes-${matchNum}-${category}`}
                                >
                                  Voir VENTE
                                </Button>
                              )}
                              {bd.achats.length > 0 && (
                                <Button
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); handleCategoryClick(event, category, stock, 'achat'); }}
                                  className="h-6 px-2 text-[10px] font-bold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40"
                                  data-testid={`tooltip-view-stock-${matchNum}-${category}`}
                                >
                                  Voir STOCK
                                </Button>
                              )}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })
            ) : (
              <p className="text-sm text-zinc-500 text-center py-2">Pas de stock</p>
            )}
          </div>
        </div>
      </div>
    );
  };


  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Package className="h-12 w-12 mb-4 opacity-50" />
        <p>Aucun stock à afficher</p>
        <p className="text-sm">Importez des billets pour voir le delta</p>
      </div>
    );
  }

  return (
    <div>
      {/* WC Filter Bar */}
      {isWC && (
        <div className="flex flex-col gap-3 mb-4 p-3 bg-zinc-100 dark:bg-zinc-800/50 rounded-lg" data-testid="delta-filters-wc" style={{ position: 'relative', zIndex: 50, overflow: 'visible' }}>
          {/* Match Number Search (replaces decade buttons) */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-zinc-500 uppercase mr-1">Matchs:</span>
            <Button
              size="sm"
              variant={matchNumSearch === '' ? 'default' : 'outline'}
              onClick={() => setMatchNumSearch('')}
              className="h-10 text-sm px-4"
              data-testid="match-range-all"
            >
              Tous
            </Button>
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                placeholder="N° match…"
                value={matchNumSearch}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                  setMatchNumSearch(v);
                }}
                className="h-10 text-base px-4 pr-9 w-56 rounded border-2 border-[#C4A052]/40 bg-zinc-900 text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#C4A052] focus:ring-2 focus:ring-[#C4A052]/30 font-mono font-bold"
                data-testid="match-num-search-input"
              />
              {matchNumSearch && (
                <button
                  type="button"
                  onClick={() => setMatchNumSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white text-sm px-1.5 rounded hover:bg-zinc-700"
                  data-testid="match-num-search-clear"
                  aria-label="Effacer"
                >✕</button>
              )}
            </div>
            {matchNumSearch && (
              <span className="text-base text-[#C4A052] font-bold ml-1">→ M{matchNumSearch}</span>
            )}

            {/* Filtre Matchs (WC) : sélection multi pour masquer M1, M5… */}
            {availableMatches.length > 0 && (
              <div className="relative ml-auto" ref={dateFilterRef}>
                <Button
                  size="sm"
                  variant={hiddenMatches.size > 0 ? 'default' : 'outline'}
                  onClick={() => setDateFilterOpen(o => !o)}
                  className={cn(
                    "h-10 text-sm px-4",
                    hiddenMatches.size > 0 && "bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400"
                  )}
                  data-testid="filter-matches-toggle-wc"
                >
                  🏟️ Matchs
                  {hiddenMatches.size > 0 && (
                    <span className="ml-2 bg-white/20 text-white px-1.5 py-0.5 rounded text-xs font-bold">
                      {hiddenMatches.size} caché{hiddenMatches.size > 1 ? 's' : ''}
                    </span>
                  )}
                  <svg className="ml-2 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </Button>
                {dateFilterOpen && (
                  <div className="absolute right-0 mt-1 bg-zinc-950 border-2 border-[#C4A052]/50 rounded-lg shadow-2xl py-1 min-w-[360px] max-h-[500px] overflow-y-auto" style={{ zIndex: 99999, top: '100%', backdropFilter: 'none', opacity: 1 }} data-testid="filter-matches-menu-wc">
                    <div className="flex gap-1 px-2 py-1.5 border-b border-zinc-700 bg-zinc-900 sticky top-0 z-10">
                      <Button size="sm" variant="ghost" onClick={showAllMatches} className="h-6 text-xs px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10">👁️ Tout afficher</Button>
                      <Button size="sm" variant="ghost" onClick={hideAllMatches} className="h-6 text-xs px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10">🙈 Tout cacher</Button>
                    </div>
                    {availableMatches.map(m => {
                      const isHidden = hiddenMatches.has(m.num);
                      return (
                        <button
                          key={m.num}
                          onClick={() => toggleMatch(m.num)}
                          className={cn(
                            "w-full flex items-center justify-between gap-2 px-3 py-2 text-xs transition-colors bg-zinc-950 hover:bg-zinc-800 text-left",
                            isHidden ? "text-zinc-500" : "text-white"
                          )}
                          data-testid={`filter-match-wc-${m.num}`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#C4A052]/20 text-[#C4A052] border border-[#C4A052]/40 min-w-[40px] text-center font-mono", isHidden && "opacity-50")}>
                              M{m.num}
                            </span>
                            <span className={cn("truncate text-xs", isHidden && "line-through")}>
                              {(m.evenement || '').replace(/\s*-\s*Match\s+\d+\s*/i, '').replace(/^M\d+\s*-?\s*/, '').slice(0, 50)}
                            </span>
                          </div>
                          <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded shrink-0",
                            isHidden ? "bg-zinc-800 text-zinc-500 border border-zinc-700" : "bg-emerald-600/40 text-emerald-200 border border-emerald-500/40"
                          )}>
                            {isHidden ? '🙈' : '👁️'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Venue Multi-Select Dropdown */}
          {venues.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-zinc-500 uppercase mr-1">Lieu:</span>
              <div className="relative" ref={venueDropdownRef}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setVenueDropdownOpen(!venueDropdownOpen)}
                  className="h-7 text-xs px-3"
                  data-testid="venue-dropdown-btn"
                >
                  {selectedVenues.size === 0 ? 'Tous les stades' : `${selectedVenues.size} stade(s)`}
                  <svg className="ml-1.5 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
                </Button>
                {venueDropdownOpen && (
                  <div className="absolute left-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl py-1 min-w-[280px] max-h-[350px] overflow-y-auto" style={{ zIndex: 9999, top: '100%' }} data-testid="venue-dropdown-menu">
                    <div className="flex gap-1 px-2 py-1.5 border-b border-zinc-700">
                      <Button size="sm" variant="ghost" onClick={selectAllVenues} className="h-6 text-xs px-2">Tout sélectionner</Button>
                      <Button size="sm" variant="ghost" onClick={clearVenues} className="h-6 text-xs px-2">Tout effacer</Button>
                    </div>
                    {venues.map(venue => (
                      <label 
                        key={venue}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-800 cursor-pointer text-xs"
                        data-testid={`venue-option-${venue.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedVenues.has(venue)}
                          onChange={() => toggleVenue(venue)}
                          className="rounded border-zinc-600 h-3.5 w-3.5"
                        />
                        <span className="text-zinc-200">{venue}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {selectedVenues.size > 0 && (
                <Button size="sm" variant="ghost" onClick={clearVenues} className="h-7 text-xs px-2 text-zinc-400">
                  Effacer
                </Button>
              )}
            </div>
          )}
          
          {/* VUE DRIVE toggle + Counter + Export Dropdown */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-zinc-500 uppercase mr-1">Vue:</span>
              <Button
                size="sm"
                variant={viewMode === 'cards' ? 'default' : 'outline'}
                onClick={() => setViewMode('cards')}
                className="h-7 text-xs px-2.5"
                data-testid="view-mode-cards"
              >
                Cartes
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'table' ? 'default' : 'outline'}
                onClick={() => setViewMode('table')}
                className="h-7 text-xs px-2.5"
                data-testid="view-mode-table"
              >
                Drive
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'detail' ? 'default' : 'outline'}
                onClick={() => setViewMode('detail')}
                className="h-7 text-xs px-2.5"
                data-testid="view-mode-detail"
              >
                Détail
              </Button>
            </div>
            {/* 🕐 Toggle "Afficher matchs passés" (WC uniquement) — masque auto 4h après coup d'envoi */}
            {isWC && (
              <Button
                size="sm"
                variant={showPastMatches ? 'default' : 'outline'}
                onClick={() => setShowPastMatches(v => !v)}
                className={cn(
                  'h-7 text-xs px-2.5',
                  showPastMatches
                    ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-100 border-zinc-600'
                    : 'text-zinc-400 border-zinc-700 hover:text-zinc-100'
                )}
                data-testid="toggle-past-matches"
                title="Les matchs sont masqués automatiquement 4h après leur coup d'envoi. Active pour les voir."
              >
                {showPastMatches ? '✓ Matchs passés' : '⏳ Matchs passés'}
              </Button>
            )}
            {(viewMode === 'table' || viewMode === 'detail') && (
              <div className="flex items-center gap-1">
                <Button size="sm" variant={tableFilter === 'all' ? 'default' : 'outline'} onClick={() => setTableFilter('all')} className="h-7 text-xs px-2.5" data-testid="table-filter-all">TOUT</Button>
                <Button size="sm" variant={tableFilter === 'need' ? 'default' : 'outline'} onClick={() => setTableFilter('need')} className="h-7 text-xs px-2 text-red-400 border-red-400/30" data-testid="table-filter-need">NEED</Button>
                <Button size="sm" variant={tableFilter === 'stock' ? 'default' : 'outline'} onClick={() => setTableFilter('stock')} className="h-7 text-xs px-2 text-emerald-400 border-emerald-400/30" data-testid="table-filter-stock">STOCK</Button>
                {viewMode === 'table' && (
                  <Button
                    size="sm"
                    variant={tableDetailed ? 'default' : 'outline'}
                    onClick={() => setTableDetailed(v => !v)}
                    className={cn(
                      "h-7 text-xs px-2.5 ml-1",
                      tableDetailed && "bg-amber-500 hover:bg-amber-600 text-black border-amber-400"
                    )}
                    data-testid="table-detailed-toggle"
                    title="Afficher le détail des quantités (ex: 3+3+6+8 au lieu de -20)"
                  >
                    {tableDetailed ? '🔍 Détaillé ON' : '🔍 Détaillé'}
                  </Button>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto text-xs text-zinc-500">
              <span>{sortedData.length}/{data.length} matchs</span>
              <div className="relative">
                <Button
                  size="sm"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  data-testid="export-delta-btn"
                  className="h-7 text-xs px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Download className="h-3 w-3 mr-1.5" />Exporter DELTA
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 top-8 z-50 bg-zinc-800 border border-zinc-600 rounded-md shadow-lg py-1 min-w-[180px]">
                    <button onClick={() => handleExportDelta('all')} data-testid="export-delta-tout" className="w-full text-left px-3 py-2 text-xs text-white hover:bg-zinc-700 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-400"></span> DELTA TOUT
                    </button>
                    <button onClick={() => handleExportDelta('need')} data-testid="export-delta-need" className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-zinc-700 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span> DELTA NEED
                    </button>
                    <button onClick={() => handleExportDelta('stock')} data-testid="export-delta-stock" className="w-full text-left px-3 py-2 text-xs text-emerald-400 hover:bg-zinc-700 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span> DELTA STOCK
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* RG Filter Bar (unchanged) */}
      {!isWC && (courts.length > 0 || sessions.length > 0) && (
        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-zinc-100 dark:bg-zinc-800/50 rounded-lg" data-testid="delta-filters">
          {/* Filtre Court */}
          {courts.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-zinc-500 uppercase mr-1">Court:</span>
              <Button
                size="sm"
                variant={filterCourt === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterCourt('all')}
                className="h-7 text-xs px-2.5"
                data-testid="filter-court-all"
              >
                Tous
              </Button>
              {courts.map(court => (
                <Button
                  key={court}
                  size="sm"
                  variant={filterCourt === court ? 'default' : 'outline'}
                  onClick={() => setFilterCourt(court)}
                  className="h-7 text-xs px-2.5"
                  data-testid={`filter-court-${court.toLowerCase()}`}
                >
                  {court}
                </Button>
              ))}
            </div>
          )}
          
          {/* Séparateur */}
          {courts.length > 0 && sessions.length > 0 && (
            <div className="w-px h-7 bg-zinc-300 dark:bg-zinc-600 self-center mx-1" />
          )}
          
          {/* Filtre Session */}
          {sessions.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-zinc-500 uppercase mr-1">Session:</span>
              <Button
                size="sm"
                variant={filterSession === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterSession('all')}
                className="h-7 text-xs px-2.5"
                data-testid="filter-session-all"
              >
                Toutes
              </Button>
              {sessions.map(sess => (
                <Button
                  key={sess}
                  size="sm"
                  variant={filterSession === sess ? 'default' : 'outline'}
                  onClick={() => setFilterSession(sess)}
                  className="h-7 text-xs px-2.5"
                  data-testid={`filter-session-${sess.toLowerCase()}`}
                >
                  {sess === 'Day' ? 'Day' : 'Evening'}
                </Button>
              ))}
            </div>
          )}

          {/* Séparateur + Filtre Dates (poussé à droite) */}
          {availableDates.length > 0 && (
            <div className="relative ml-auto" ref={dateFilterRef}>
              <Button
                size="sm"
                variant={hiddenDates.size > 0 ? 'default' : 'outline'}
                onClick={() => setDateFilterOpen(o => !o)}
                className={cn(
                  "h-7 text-xs px-2.5",
                  hiddenDates.size > 0 && "bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-400"
                )}
                data-testid="filter-dates-toggle"
                title="Masquer / afficher certaines dates"
              >
                📅 Dates
                {hiddenDates.size > 0 && (
                  <span className="ml-1.5 bg-white/20 text-white px-1.5 py-0.5 rounded text-[10px] font-bold">
                    {hiddenDates.size} cachée{hiddenDates.size > 1 ? 's' : ''}
                  </span>
                )}
                <svg className="ml-1.5 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
              </Button>
              {dateFilterOpen && (
                <div className="absolute right-0 mt-1 bg-zinc-950 border-2 border-[#C4A052]/50 rounded-lg shadow-2xl py-1 min-w-[260px] max-h-[450px] overflow-y-auto" style={{ zIndex: 99999, top: '100%', backdropFilter: 'none', opacity: 1 }} data-testid="filter-dates-menu">
                  <div className="flex gap-1 px-2 py-1.5 border-b border-zinc-700 bg-zinc-900 sticky top-0 z-10">
                    <Button size="sm" variant="ghost" onClick={showAllDates} className="h-6 text-xs px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10">👁️ Tout afficher</Button>
                    <Button size="sm" variant="ghost" onClick={hideAllDates} className="h-6 text-xs px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10">🙈 Tout cacher</Button>
                  </div>
                  {availableDates.map(date => {
                    const isHidden = hiddenDates.has(date);
                    return (
                      <button
                        key={date}
                        onClick={() => toggleDate(date)}
                        className={cn(
                          "w-full flex items-center justify-between gap-2 px-3 py-2 text-xs transition-colors bg-zinc-950 hover:bg-zinc-800",
                          isHidden ? "text-zinc-500" : "text-white"
                        )}
                        data-testid={`filter-date-${date}`}
                      >
                        <span className={cn("font-mono font-bold", isHidden && "line-through")}>{formatDateShort(date)}</span>
                        <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded",
                          isHidden ? "bg-zinc-800 text-zinc-500 border border-zinc-700" : "bg-emerald-600/40 text-emerald-200 border border-emerald-500/40"
                        )}>
                          {isHidden ? '🙈 Cachée' : '👁️ Visible'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Counter + Export Dropdown */}
          <div className="flex items-center gap-2 ml-auto text-xs text-zinc-500">
            <span>{sortedData.length}/{data.length} matchs</span>
            <div className="relative">
              <Button
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                data-testid="export-delta-btn-rg"
                className="h-7 text-xs px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Download className="h-3 w-3 mr-1.5" />Exporter DELTA
              </Button>
              {showExportMenu && (
                <div className="absolute right-0 top-8 z-50 bg-zinc-800 border border-zinc-600 rounded-md shadow-lg py-1 min-w-[180px]">
                  <button
                    onClick={() => handleExportDelta('all')}
                    className="w-full text-left px-3 py-2 text-xs text-white hover:bg-zinc-700 flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full bg-blue-400"></span> DELTA TOUT
                  </button>
                  <button
                    onClick={() => handleExportDelta('need')}
                    className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-zinc-700 flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full bg-red-500"></span> DELTA NEED
                  </button>
                  <button
                    onClick={() => handleExportDelta('stock')}
                    className="w-full text-left px-3 py-2 text-xs text-emerald-400 hover:bg-zinc-700 flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span> DELTA STOCK
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* VUE DRIVE: Table View */}
      {isWC && viewMode === 'detail' && (
        <DeltaDetailView data={data} allTickets={allTickets} project={project} />
      )}

      {isWC && viewMode === 'table' && (() => {
        // Collect all unique categories
        const allCats = new Set();
        sortedData.forEach(e => {
          if (e.stocks) Object.keys(e.stocks).forEach(c => allCats.add(c));
        });
        const catOrd = (cat) => {
          const c = cat.toUpperCase().replace(/[ÉE]/g, 'E').replace(/CATEGORIE\s*/g, 'CAT').trim();
          if (c === 'GOLD') return 0;
          if (/^CAT\s*1$/i.test(c)) return 1;
          if (/^CAT\s*2$/i.test(c)) return 2;
          if (/^CAT\s*3$/i.test(c)) return 3;
          if (/^CAT\s*4$/i.test(c)) return 4;
          return 100;
        };
        const cats = Array.from(allCats).sort((a, b) => {
          const oa = catOrd(a), ob = catOrd(b);
          return oa !== ob ? oa - ob : a.localeCompare(b);
        });
        
        // Group by match
        const matchMap = {};
        sortedData.forEach(event => {
          const mn = event.match_num || extractMatchNumber(event.evenement);
          if (!matchMap[mn]) {
            matchMap[mn] = { matchNum: mn, name: event.evenement || '', dateEvenement: event.date_evenement || '', lieu: event.lieu || '', ville: event.ville || '', stocks: {} };
          }
          if (event.stocks) {
            Object.entries(event.stocks).forEach(([cat, stock]) => {
              matchMap[mn].stocks[cat] = (matchMap[mn].stocks[cat] || 0) + stock;
            });
          }
        });
        
        let rows = Object.values(matchMap).sort((a, b) => a.matchNum - b.matchNum).map(m => {
          const catVals = cats.map(c => m.stocks[c] || 0);
          let filteredCats;
          if (tableFilter === 'need') filteredCats = catVals.map(v => v < 0 ? v : 0);
          else if (tableFilter === 'stock') filteredCats = catVals.map(v => v > 0 ? v : 0);
          else filteredCats = catVals;
          const total = filteredCats.reduce((s, v) => s + v, 0);
          return { matchNum: m.matchNum, name: m.name, dateEvenement: m.dateEvenement, lieu: m.lieu, ville: m.ville, total, cats: filteredCats };
        });
        
        // Filter out rows where all cats are 0 (for need/stock)
        if (tableFilter !== 'all') rows = rows.filter(r => r.cats.some(v => v !== 0));
        
        // Find which cat columns have data
        const keepCol = cats.map((_, ci) => rows.some(r => r.cats[ci] !== 0));
        const visibleCats = cats.filter((_, ci) => keepCol[ci]);
        
        return (
          <div className="relative">
            {/* Crosshair indicator */}
            {hoveredCell && (
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-50 bg-zinc-900 border border-zinc-500 rounded px-3 py-1 text-sm font-bold text-white whitespace-nowrap shadow-lg pointer-events-none">
                <span className="text-amber-400">M{hoveredCell.matchLabel}</span>
                <span className="text-zinc-500 mx-1.5">|</span>
                <span className="text-sky-400">{hoveredCell.catLabel}</span>
                {hoveredCell.value !== undefined && hoveredCell.value !== 0 && (
                  <>
                    <span className="text-zinc-500 mx-1.5">|</span>
                    <span className={hoveredCell.value < 0 ? 'text-red-400' : 'text-emerald-400'}>{hoveredCell.value}</span>
                  </>
                )}
              </div>
            )}
            <div className="overflow-auto rounded-lg border border-zinc-700" style={{ maxHeight: '65vh' }} data-testid="delta-table-view" onMouseLeave={() => setHoveredCell(null)}>
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-zinc-800 border-b-2 border-zinc-600">
                    <th className="sticky left-0 bg-zinc-800 z-30 px-3 py-2.5 text-left text-zinc-300 font-bold w-16 border-r border-zinc-700">Match</th>
                    <th className="px-3 py-2.5 text-left text-zinc-300 font-bold min-w-[220px] border-r border-zinc-700">Nom du Match</th>
                    <th className="px-3 py-2.5 text-center text-zinc-300 font-bold w-16 border-r border-zinc-700">Total</th>
                    {visibleCats.map((cat, ci) => (
                      <th key={cat} className={`px-2.5 py-2.5 text-center font-bold whitespace-nowrap ${ci < visibleCats.length - 1 ? 'border-r border-zinc-700/50' : ''} ${hoveredCell && hoveredCell.col === ci ? 'text-sky-300 bg-sky-500/15 text-sm' : 'text-zinc-300 text-xs'} transition-all`}>{cat}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => {
                    const isRowHovered = hoveredCell && hoveredCell.row === ri;
                    const isRowBeforeHovered = hoveredCell && ri < hoveredCell.row;
                    return (
                      <React.Fragment key={row.matchNum}>
                      <tr className={`border-b border-zinc-700/60 transition-colors ${isRowHovered ? 'bg-zinc-700/60' : ri % 2 === 0 ? 'bg-zinc-900/80' : 'bg-zinc-800/40'}`}>
                        <td className={`sticky left-0 z-10 px-3 py-2 font-bold border-r border-zinc-700 transition-all ${isRowHovered ? 'text-amber-400 bg-zinc-700/80 text-sm' : 'text-white text-xs'}`} style={!isRowHovered ? { backgroundColor: ri % 2 === 0 ? 'rgb(24 24 27 / 0.9)' : 'rgb(39 39 42 / 0.6)' } : { backgroundColor: 'rgb(63 63 70 / 0.8)' }}>M{row.matchNum}</td>
                        <td className={`px-3 py-2 truncate max-w-[280px] border-r border-zinc-700/50 ${isRowHovered ? 'text-white' : 'text-zinc-300'}`} title={(() => { const e = formatEventNameWithDoute(row.name, row.matchNum, row.dateEvenement, { lieu: row.lieu, ville: row.ville }); return e.doute ? `${e.text} ⚠ DOUTE — ${e.reason || ''}` : e.text; })()}>
                          {(() => {
                            const ev = formatEventNameWithDoute(row.name, row.matchNum, row.dateEvenement, { lieu: row.lieu, ville: row.ville });
                            return (<>
                              {ev.text}
                              {ev.doute && (
                                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40" title={ev.reason} data-testid={`drive-doute-${row.matchNum}`}>⚠ DOUTE</span>
                              )}
                            </>);
                          })()}
                        </td>
                        <td className={`px-3 py-2 text-center font-bold border-r border-zinc-700/50 ${row.total < 0 ? 'text-red-400 bg-red-500/8' : row.total > 0 ? 'text-emerald-400 bg-emerald-500/8' : 'text-zinc-600'}`}>
                          {row.total !== 0 ? row.total : ''}
                        </td>
                        {visibleCats.map((cat, ci) => {
                          const idx = cats.indexOf(cat);
                          const val = row.cats[idx];
                          const clickable = val !== 0 && onCategoryClick;
                          const isCellHovered = isRowHovered && hoveredCell && hoveredCell.col === ci;
                          // Crosshair: highlight row cells ONLY up to hovered column
                          const isRowPath = isRowHovered && hoveredCell && ci < hoveredCell.col;
                          // Crosshair: highlight column cells ONLY up to hovered row
                          const isColPath = hoveredCell && hoveredCell.col === ci && isRowBeforeHovered;
                          return (
                            <td 
                              key={cat} 
                              className={`px-2.5 py-2 text-center font-mono transition-colors ${
                                isCellHovered ? 'bg-sky-500/30 ring-2 ring-sky-400 text-white font-bold text-sm' :
                                isColPath ? 'bg-sky-500/20' :
                                isRowPath ? 'bg-amber-500/15' :
                                val < 0 ? 'bg-red-500/5' : val > 0 ? 'bg-emerald-500/5' : ''
                              } ${val < 0 ? 'text-red-400' : val > 0 ? 'text-emerald-400' : ''} ${
                                ci < visibleCats.length - 1 ? 'border-r border-zinc-800/50' : ''
                              } ${clickable ? 'cursor-pointer hover:underline' : ''}`}
                              style={isColPath ? { boxShadow: 'inset 2px 0 0 rgba(56,189,248,0.4), inset -2px 0 0 rgba(56,189,248,0.4)' } : isRowPath ? { boxShadow: 'inset 0 2px 0 rgba(245,158,11,0.3), inset 0 -2px 0 rgba(245,158,11,0.3)' } : undefined}
                              onMouseEnter={() => setHoveredCell({ row: ri, col: ci, matchLabel: row.matchNum, catLabel: cat, value: val })}
                              onClick={async () => {
                                if (clickable) {
                                  if (viewMode === 'detail') {
                                    // Toggle expand
                                    const cellKey = `${row.matchNum}_${cat}`;
                                    if (expandedCell === cellKey) {
                                      setExpandedCell(null);
                                      setExpandedTickets([]);
                                    } else {
                                      setExpandedCell(cellKey);
                                      setLoadingDetail(true);
                                      try {
                                        const res = await fetch(`/api/tickets?project=${encodeURIComponent(project)}&limit=500&match_num=${row.matchNum}&categorie=${encodeURIComponent(cat)}`);
                                        const data = await res.json();
                                        setExpandedTickets(data.tickets || data || []);
                                      } catch (e) { setExpandedTickets([]); }
                                      setLoadingDetail(false);
                                    }
                                  } else {
                                    handleSaveScroll();
                                    onCategoryClick({
                                      viewType: val < 0 ? 'buy' : 'sell',
                                      eventName: row.name,
                                      matchNum: row.matchNum,
                                      category: cat,
                                      stock: val,
                                      session: '',
                                      lieu: row.lieu || ''
                                    });
                                  }
                                }
                              }}
                              data-testid={val !== 0 ? `drive-cell-m${row.matchNum}-${cat.replace(/\s+/g, '-').toLowerCase()}` : undefined}
                            >
                              {val !== 0 ? (
                                tableDetailed ? (() => {
                                  const bd = getBreakdown(row.matchNum, cat);
                                  // NEED (val<0) → ventes; STOCK (val>0) → achats
                                  const arr = val < 0 ? bd.ventes : bd.achats;
                                  if (!arr || arr.length === 0) return val;
                                  if (arr.length === 1) return val;
                                  return (
                                    <span className="text-xs font-bold leading-tight">
                                      {arr.map((item, i) => {
                                        // item est { qty, partial, remaining } pour ventes, { qty } pour achats
                                        const showQty = (val < 0)
                                          ? (item.remaining ?? item.qty ?? item)
                                          : (item.qty ?? item);
                                        const hasPartial = val < 0 && item.partial > 0;
                                        return (
                                          <React.Fragment key={i}>
                                            {i > 0 && <span className="text-zinc-500 mx-0.5">+</span>}
                                            {hasPartial ? (
                                              <span className="inline-flex items-baseline">
                                                <span className="text-sky-300">{item.partial}</span>
                                                <span className="text-zinc-500 mx-px">+</span>
                                                <span>{item.remaining}</span>
                                              </span>
                                            ) : (
                                              <span>{Math.abs(showQty)}</span>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </span>
                                  );
                                })() : val
                              ) : ''}
                            </td>
                          );
                        })}
                      </tr>
                      {/* Expanded detail row */}
                      {viewMode === 'detail' && expandedCell && expandedCell.startsWith(`${row.matchNum}_`) && (
                        <tr>
                          <td colSpan={visibleCats.length + 3} className="p-0">
                            <div className="bg-zinc-900 border-t border-b border-[#C4A052]/30 px-4 py-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-bold text-[#C4A052]">
                                  M{row.matchNum} — {expandedCell.split('_').slice(1).join('_')} — {expandedTickets.length} ticket(s)
                                </span>
                                <button onClick={() => { setExpandedCell(null); setExpandedTickets([]); }} className="text-xs text-zinc-500 hover:text-white">✕ Fermer</button>
                              </div>
                              {loadingDetail && <div className="text-sm text-zinc-500">Chargement...</div>}
                              {!loadingDetail && expandedTickets.length === 0 && <div className="text-sm text-zinc-500">Aucun ticket trouvé</div>}
                              {!loadingDetail && expandedTickets.length > 0 && (() => {
                                const isCancelled = (t) => {
                                  const s = (t.statut || '').toUpperCase();
                                  return s === 'ANNULEE' || s === 'ANNULÉ' || s === 'ANNULE';
                                };
                                const activeTickets = expandedTickets.filter(t => !isCancelled(t));
                                const venteCount = activeTickets.filter(t => t.type === 'VENTE').length;
                                const venteQtyTotal = activeTickets.filter(t => t.type === 'VENTE').reduce((s, t) => s + (parseInt(t.qty, 10) || 1), 0);
                                const achatCount = activeTickets.filter(t => t.type === 'ACHAT').length;
                                return (
                                  <div>
                                    <div className="flex items-center gap-4 mb-2 text-xs">
                                      <span className="text-red-400 font-bold">VENTE: {venteCount} ligne(s) = {venteQtyTotal} billet(s) à fournir</span>
                                      <span className="text-emerald-400 font-bold">ACHAT: {achatCount} en stock</span>
                                      {venteQtyTotal > achatCount && <span className="text-yellow-400 font-bold">MANQUE {venteQtyTotal - achatCount} 🚨</span>}
                                      {venteQtyTotal <= achatCount && <span className="text-emerald-300">STOCK OK (+{achatCount - venteQtyTotal})</span>}
                                    </div>
                                <div className="max-h-[300px] overflow-auto">
                                  <table className="w-full text-xs">
                                    <thead className="bg-zinc-800 sticky top-0">
                                      <tr>
                                        <th className="px-2 py-1 text-left text-zinc-400">Type</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Ref Vente</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Client</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Qty</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Prix</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Block</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Rang</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Place</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Source</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Email Compte</th>
                                        <th className="px-2 py-1 text-left text-zinc-400">Attribué</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expandedTickets.sort((a, b) => {
                                        if (a.type !== b.type) return a.type === 'VENTE' ? -1 : 1;
                                        const ab = parseInt(a.block, 10) || 99999;
                                        const bb2 = parseInt(b.block, 10) || 99999;
                                        if (ab !== bb2) return ab - bb2;
                                        const ar = parseInt(a.rang, 10) || 99999;
                                        const br = parseInt(b.rang, 10) || 99999;
                                        if (ar !== br) return ar - br;
                                        return (parseInt(a.place, 10) || 99999) - (parseInt(b.place, 10) || 99999);
                                      }).map((t, ti) => (
                                        <tr key={ti} className={`border-t border-zinc-800 ${t.type === 'VENTE' ? 'bg-red-900/10' : 'bg-emerald-900/10'}`}>
                                          <td className="px-2 py-1">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${t.type === 'VENTE' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'}`}>{t.type}</span>
                                          </td>
                                          <td className="px-2 py-1 font-mono text-zinc-300">{t.ref_vente || '-'}</td>
                                          <td className="px-2 py-1 text-zinc-300">{t.nom_client || '-'}</td>
                                          <td className="px-2 py-1 text-zinc-300">{t.qty || 1}</td>
                                          <td className="px-2 py-1 text-[#C4A052]">{t.prix ? `${t.prix}€` : '-'}</td>
                                          <td className="px-2 py-1 text-[#C4A052] font-bold">{t.block || ''}</td>
                                          <td className="px-2 py-1 text-[#C4A052] font-bold">{t.rang || ''}</td>
                                          <td className="px-2 py-1 text-[#C4A052] font-bold">{t.place || ''}</td>
                                          <td className="px-2 py-1 text-zinc-400">{t.source || '-'}</td>
                                          <td className="px-2 py-1 text-zinc-500 truncate max-w-[150px]">{t.email_compte || '-'}</td>
                                          <td className="px-2 py-1">
                                            {t.attribue ? <span className="text-emerald-400 text-[10px] font-bold">OUI</span> : <span className="text-zinc-500 text-[10px]">Non</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
      
      {/* Card View (default) */}
      {(viewMode === 'cards' || !isWC) && (() => {
        // Determine phase group for each card
        const getPhaseGroup = (event) => {
          const pn = extractPhaseName(event.evenement);
          if (!pn) return 'Autre';
          if (/1st\s*Round/i.test(pn)) return '1st Round';
          if (/2nd\s*Round/i.test(pn)) return '2nd Round';
          if (/3rd\s*Round/i.test(pn)) return '3rd Round';
          if (/4th\s*Round/i.test(pn)) return '4th Round';
          if (/Quarterfinal/i.test(pn)) return 'Quarterfinals';
          if (/Semi/i.test(pn)) return 'Semifinals';
          if (/Final/i.test(pn) && !/Semi/i.test(pn)) return 'Finals';
          return 'Autre';
        };
        
        // Check if this is a RG delta (at least one card has Chatrier/Lenglen)
        const isRGDelta = sortedData.some(e => 
          (e.session && e.session !== '-') || 
          (e.lieu && (e.lieu.includes('Chatrier') || e.lieu.includes('Lenglen')))
        );
        
        if (!isRGDelta) {
          // Non-RG: flat grid as before
          return (
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {sortedData.map((event, index) => renderDeltaCard(event, index))}
            </div>
          );
        }
        
        // RG: group by phase
        const phaseOrder = ['1st Round', '2nd Round', '3rd Round', '4th Round', 'Quarterfinals', 'Semifinals', 'Finals', 'Autre'];
        const grouped = {};
        sortedData.forEach((event, index) => {
          const group = getPhaseGroup(event);
          if (!grouped[group]) grouped[group] = [];
          grouped[group].push({ event, index });
        });
        
        const phaseLabels = {
          '1st Round': '1er Tour',
          '2nd Round': '2ème Tour',
          '3rd Round': '3ème Tour',
          '4th Round': '4ème Tour',
          'Quarterfinals': 'Quarts de Finale',
          'Semifinals': 'Demi-Finales',
          'Finals': 'Finales',
          'Autre': 'Autre',
        };
        
        return phaseOrder.filter(p => grouped[p]?.length > 0).map((phase, pi) => {
          // Sub-group by date within each phase
          const dateGroups = {};
          grouped[phase].forEach(({ event, index }) => {
            const date = event.date || event.date_evenement || '';
            // Parse date to get just the day (DD/MM/YYYY)
            const dateKey = date || 'Sans date';
            if (!dateGroups[dateKey]) dateGroups[dateKey] = [];
            dateGroups[dateKey].push({ event, index });
          });
          
          // Sort dates chronologically
          const sortedDates = Object.keys(dateGroups).sort((a, b) => {
            const parseDate = (d) => {
              const parts = d.match(/(\d{2})\/(\d{2})\/(\d{4})/);
              if (parts) return new Date(parts[3], parts[2] - 1, parts[1]);
              // ISO format YYYY-MM-DD
              const iso = d.match(/(\d{4})-(\d{2})-(\d{2})/);
              if (iso) return new Date(iso[1], iso[2] - 1, iso[3]);
              return new Date(d);
            };
            try { return parseDate(a) - parseDate(b); } catch { return 0; }
          });

          // Format date for display (DD/MM/YYYY)
          const formatDateLabel = (d) => {
            const iso = d.match(/(\d{4})-(\d{2})-(\d{2})/);
            if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
            return d;
          };
          
          return (
            <div key={phase} className={pi > 0 ? 'mt-4' : ''}>
              {/* Phase header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-[#C4A052]/30" />
                <span className="text-sm font-bold text-[#C4A052] tracking-wider uppercase whitespace-nowrap">
                  {phaseLabels[phase] || phase}
                </span>
                <div className="h-px flex-1 bg-[#C4A052]/30" />
              </div>
              {/* Date rows */}
              {sortedDates.map((dateKey) => (
                <div key={dateKey} className="mb-4">
                  <div className="text-lg text-[#C4A052] font-bold mb-2 pl-1">{formatDateLabel(dateKey)}</div>
                  <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {dateGroups[dateKey].map(({ event, index }) => renderDeltaCard(event, index))}
                  </div>
                </div>
              ))}
            </div>
          );
        });
      })()}
      
      <StadiumPlanModal
        open={stadiumPlanOpen}
        onOpenChange={setStadiumPlanOpen}
        stadium={selectedStadium}
        matchInfo={selectedMatchInfo}
      />
    </div>
  );
};

export default DeltaView;
